import React, { useState, useRef, useEffect } from "react";
import { Play, Link2, Check, Stethoscope, Presentation, ShieldCheck, ArrowRight, MonitorPlay } from "lucide-react";
import { publicOrigin } from "@/lib/publicUrl";

const BG = "hsl(244,47%,6%)";
const SURFACE = "hsl(244,30%,11%)";
const ACCENT = "hsl(244,76%,64%)";

const VIDEOS = [
  {
    key: "live-workflow",
    label: "Live Care Manager Workflow",
    tag: "REAL SCREEN RECORDING",
    icon: MonitorPlay,
    src: "/media/gomco-live-demo.mp4",
    desc: "Not slides — a real screen recording. Maya shares her screen and works three tasks live: triaging her population command center, opening a Member 360, and generating an AI clinical snapshot on the spot, all while narrating.",
  },
  {
    key: "care-manager",
    label: "Care Manager Demo",
    tag: "A DAY IN THE LIFE",
    icon: Stethoscope,
    src: "/media/gomco-care-manager-demo.mp4",
    desc: "Maya, a care manager, narrates three functions she performs every day: triaging her population command center, opening a Member 360 and generating an AI clinical summary & care plan, then closing quality gaps and messaging members securely.",
  },
  {
    key: "pitch-deck",
    label: "Platform Pitch & ROI",
    tag: "THE BUSINESS CASE",
    icon: Presentation,
    src: "/media/gomco-pitch-deck.mp4",
    desc: "A guided pitch of the platform with a concrete savings scenario for Trillium — including how AI-assisted documentation alone could reclaim ~10,000 care-manager hours (~$450,000) per year.",
  },
];

function VideoCard({ v }) {
  const videoRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const link = `${publicOrigin()}${v.src}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div
      className="rounded-2xl border overflow-hidden flex flex-col"
      style={{ background: SURFACE, borderColor: "rgba(255,255,255,0.08)" }}
      data-testid={`demo-video-card-${v.key}`}
    >
      <div className="p-2">
        <video
          ref={videoRef}
          controls
          playsInline
          preload="metadata"
          className="w-full rounded-xl bg-black aspect-video"
          data-testid={`demo-video-${v.key}`}
        >
          <source src={v.src} type="video/mp4" />
        </video>
      </div>
      <div className="px-6 pb-6 pt-3 flex flex-col gap-4 flex-1">
        <div className="flex items-center gap-3">
          <span
            className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "rgba(129,140,248,0.15)", color: ACCENT }}
          >
            <v.icon className="h-5 w-5" />
          </span>
          <div>
            <p className="text-[10px] font-bold tracking-[0.22em]" style={{ color: ACCENT }}>{v.tag}</p>
            <h3 className="font-display font-bold text-lg text-white leading-tight">{v.label}</h3>
          </div>
        </div>
        <p className="text-sm text-white/60 leading-relaxed flex-1">{v.desc}</p>
        <div className="flex items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: "rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.25)" }}>
          <Link2 className="h-4 w-4 shrink-0" style={{ color: ACCENT }} />
          <input
            readOnly
            value={link}
            onFocus={(e) => e.target.select()}
            className="flex-1 bg-transparent text-xs text-white/70 focus:outline-none truncate"
            data-testid={`demo-link-${v.key}`}
          />
          <button
            onClick={copy}
            className="shrink-0 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors flex items-center gap-1.5"
            style={{ background: copied ? "rgba(99,102,241,0.15)" : ACCENT, color: copied ? "#6366f1" : "white" }}
            data-testid={`demo-copy-${v.key}`}
          >
            {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <>Copy link</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TrilliumDemoVideos() {
  useEffect(() => { document.title = "Go MCO — Demo Videos for Trillium"; }, []);

  return (
    <div className="min-h-screen w-full" style={{ background: BG }} data-testid="trillium-demo-videos">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-14">
        {/* Header */}
        <div className="flex items-center gap-2.5 mb-12">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center font-display font-extrabold text-sm text-white" style={{ background: ACCENT }}>
            GM
          </div>
          <div className="leading-tight">
            <p className="font-display font-extrabold tracking-tight text-white text-base">Go MCO</p>
            <p className="text-[9px] uppercase tracking-[0.2em] text-white/40">Managed Care, In Motion</p>
          </div>
        </div>

        <span className="text-[11px] font-bold tracking-[0.25em]" style={{ color: ACCENT }}>FOR TRILLIUM HEALTH RESOURCES</span>
        <h1 className="mt-3 font-display font-extrabold text-white text-4xl sm:text-5xl leading-[1.05] max-w-3xl">
          See the platform in action.
        </h1>
        <p className="mt-4 text-white/60 text-base max-w-2xl leading-relaxed">
          Three short, narrated walkthroughs — a live screen recording of a real care-manager workflow, a day-in-the-life demo, and the platform's business case for Trillium.
          Submit these alongside your live demo link.
        </p>

        {/* Videos */}
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          {VIDEOS.map((v) => <VideoCard key={v.key} v={v} />)}
        </div>

        {/* CTA row */}
        <div className="mt-12 flex flex-wrap items-center gap-4">
          <a
            href="/demo"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
            style={{ background: ACCENT }}
            data-testid="demo-videos-live-demo-cta"
          >
            <Play className="h-4 w-4" /> Launch the live demo <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href="/showcase"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white/80 border transition-colors hover:text-white"
            style={{ borderColor: "rgba(255,255,255,0.15)" }}
            data-testid="demo-videos-showcase-cta"
          >
            View the full showcase
          </a>
        </div>

        <div className="mt-14 flex items-center gap-2 text-white/40 text-xs">
          <ShieldCheck className="h-4 w-4" />
          Built to HIPAA · SOC 2 · NIST 800-53 standards · U.S.-onshore
        </div>
      </div>
    </div>
  );
}
